// ABOUTME: Authenticated home page as a focused daily briefing
// ABOUTME: Compact level bar, single primary action, contextual nudges

import {
  BookOpenIcon,
  CalendarIcon,
  CameraIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  FireIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageTransition, StaggerChild } from '@/components/layout/PageTransition'
import DailySuggestionCard from '@/components/suggestions/DailySuggestionCard'
import WearLogFlow from '@/components/wardrobe/WearLogFlow'
import {
  EXPERIENCE_PER_LEVEL,
  getLevelProgress,
  getLevelTitle,
  getXPToNextLevel,
} from '@/constants/gamification'
import { getChallengesByLevel } from '@/content/challenges'
import { getAvailableModules } from '@/content/modules'
import { useGameStore } from '@/lib/store/gameStore'
import { useModuleStore } from '@/lib/store/moduleStore'
import { useWardrobeStore } from '@/lib/store/wardrobeStore'
import { useWearLogStore } from '@/lib/store/wearLogStore'
import { getPersonalizationLabel } from '@/lib/utils/personalization'
import { getChallengeTracker } from '@/services/ChallengeTracker'
import {
  acceptSuggestion,
  getSuggestionsForToday,
  skipSuggestion,
} from '@/services/DailySuggestionService'
import { getNeglectedItems } from '@/services/WardrobeAnalysisService'

export default function HomePage() {
  const { level, experiencePoints, totalChallengesCompleted, colorProfile, onboarding } =
    useGameStore()
  const profileLabel = getPersonalizationLabel(colorProfile, onboarding)
  const { items } = useWardrobeStore()
  const { hasLoggedToday, getStreak, logs } = useWearLogStore()
  const { completedModules } = useModuleStore()
  const [showWearLog, setShowWearLog] = useState(false)

  const loggedToday = hasLoggedToday()
  const streak = getStreak()
  const suggestions = items.length >= 3 ? getSuggestionsForToday() : []
  const levelTitle = getLevelTitle(experiencePoints)
  const progress = getLevelProgress(experiencePoints)
  const xpToNext = getXPToNextLevel(experiencePoints)
  const currentLevelXP = experiencePoints % EXPERIENCE_PER_LEVEL

  const neglectedItems = getNeglectedItems(items, logs)
  const availableModules = getAvailableModules(level, completedModules)
  const uncompletedModules = availableModules.filter((m) => !completedModules.includes(m.id))

  const [activeChallengeCount, setActiveChallengeCount] = useState(0)
  useEffect(() => {
    const loadActiveChallenges = async () => {
      const levelChallenges = getChallengesByLevel(level)
      const tracker = getChallengeTracker()
      let count = 0
      for (const c of levelChallenges) {
        const p = await tracker.getProgress(c.id)
        if (p && p.status === 'in_progress') count++
      }
      setActiveChallengeCount(count)
    }
    loadActiveChallenges()
  }, [level])

  const nudges = buildNudges({
    activeChallenges: activeChallengeCount,
    uncompletedModules: uncompletedModules.length,
    neglectedItems: neglectedItems.length,
    totalItems: items.length,
    totalChallengesCompleted,
  })

  return (
    <PageTransition className="min-h-screen bg-bg pb-24">
      <StaggerChild className="px-5 pt-8 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-overline mb-1">Level {level}</p>
            <h1 className="text-2xl font-semibold tracking-tight text-fg-primary">{levelTitle}</h1>
          </div>
          <div className="flex items-center gap-3">
            {streak > 0 && (
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-100 dark:bg-orange-900/30">
                <FireIcon className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                  {streak}
                </span>
              </div>
            )}
            <Link
              to="/profile"
              className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center"
            >
              <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                {level}
              </span>
            </Link>
          </div>
        </div>

        <div className="w-full bg-surface rounded-full h-1.5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-xp to-emerald-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-1.5 text-xs text-fg-tertiary">
          {currentLevelXP} / {EXPERIENCE_PER_LEVEL} XP &middot; {xpToNext} to next level
        </p>
      </StaggerChild>

      <StaggerChild className="px-5 py-6">
        {!loggedToday ? (
          <button
            type="button"
            onClick={() => setShowWearLog(true)}
            className="w-full btn-primary py-4 rounded-lg text-base font-semibold inline-flex items-center justify-center gap-2"
          >
            <CalendarIcon className="w-5 h-5" />
            Log Today's Outfit
          </button>
        ) : suggestions.length > 0 ? (
          <div>
            <p className="text-overline mb-1">Today's Picks</p>
            {profileLabel && (
              <p className="text-xs text-purple-600 dark:text-purple-400 mb-3">{profileLabel}</p>
            )}
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
              {suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.items.map((i) => i.id).join('-')}
                  className="min-w-[280px] snap-start"
                >
                  <DailySuggestionCard
                    suggestion={suggestion}
                    index={index}
                    onAccept={() => acceptSuggestion(suggestion)}
                    onSkip={() => skipSuggestion(suggestion)}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-surface rounded-lg shadow-glass p-6 text-center">
            <CameraIcon className="w-8 h-8 mx-auto mb-3 text-fg-tertiary" />
            <p className="text-sm font-semibold text-fg-primary mb-1">Outfit logged for today</p>
            <Link to="/wardrobe" className="text-xs text-primary-600 font-medium">
              Scan more items to unlock suggestions
            </Link>
          </div>
        )}
      </StaggerChild>

      {items.length === 0 && (
        <div className="px-5 pb-6">
          <Link
            to="/scanner"
            className="block w-full bg-gradient-to-r from-primary to-accent rounded-lg p-6 text-white"
          >
            <div className="flex items-center gap-4">
              <CameraIcon className="w-10 h-10 flex-shrink-0 opacity-90" />
              <div>
                <p className="font-semibold text-base">Build Your Wardrobe</p>
                <p className="text-sm opacity-80 mt-0.5">Scan your first item to get started</p>
              </div>
              <ChevronRightIcon className="w-5 h-5 ml-auto opacity-60" />
            </div>
          </Link>
        </div>
      )}

      {nudges.length > 0 && (
        <StaggerChild className="px-5 pb-6">
          <p className="text-overline mb-3">Up Next</p>
          <div className="space-y-3">
            {nudges.map((nudge) => (
              <Link
                key={nudge.href}
                to={nudge.href}
                className="flex items-center gap-4 bg-surface rounded-lg shadow-glass p-4 transition-all duration-200 active:scale-[0.98]"
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${nudge.iconBg}`}
                >
                  <nudge.icon className={`w-5 h-5 ${nudge.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-fg-primary">{nudge.title}</p>
                  <p className="text-xs text-fg-secondary truncate">{nudge.subtitle}</p>
                </div>
                <ChevronRightIcon className="w-4 h-4 text-fg-tertiary flex-shrink-0" />
              </Link>
            ))}
          </div>
        </StaggerChild>
      )}

      {showWearLog && <WearLogFlow onClose={() => setShowWearLog(false)} />}
    </PageTransition>
  )
}

interface Nudge {
  icon: React.ComponentType<{ className: string }>
  title: string
  subtitle: string
  href: string
  iconBg: string
  iconColor: string
}

function buildNudges(data: {
  activeChallenges: number
  uncompletedModules: number
  neglectedItems: number
  totalItems: number
  totalChallengesCompleted: number
}): Nudge[] {
  const nudges: Nudge[] = []

  if (data.activeChallenges > 0) {
    nudges.push({
      icon: ViewfinderCircleIcon,
      title: `${data.activeChallenges} active challenge${data.activeChallenges > 1 ? 's' : ''}`,
      subtitle: 'Continue where you left off',
      href: '/challenges',
      iconBg: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-500',
    })
  }

  if (data.uncompletedModules > 0) {
    nudges.push({
      icon: BookOpenIcon,
      title: `${data.uncompletedModules} module${data.uncompletedModules > 1 ? 's' : ''} available`,
      subtitle: 'Learn something new today',
      href: '/education',
      iconBg: 'bg-sky-100 dark:bg-sky-900/30',
      iconColor: 'text-sky-500',
    })
  }

  if (data.neglectedItems > 0) {
    nudges.push({
      icon: ExclamationTriangleIcon,
      title: `${data.neglectedItems} neglected item${data.neglectedItems > 1 ? 's' : ''}`,
      subtitle: "Items you haven't worn in 30+ days",
      href: '/insights',
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-500',
    })
  }

  if (data.totalItems > 0 && data.activeChallenges === 0 && data.totalChallengesCompleted === 0) {
    nudges.push({
      icon: ViewfinderCircleIcon,
      title: 'Start your first challenge',
      subtitle: 'Earn XP by completing style tasks',
      href: '/challenges',
      iconBg: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-500',
    })
  }

  return nudges.slice(0, 3)
}
